import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, ChevronDown, LogOut } from 'lucide-react';
import USER_MENU_LINKS from '../config/userMenuLinks';
import { useTranslation } from '../hooks/useTranslation';

interface UserDropdownProps {
  username: string;
  role: string;
  onLogout: () => void;
  logoutLabel: string;
  flat?: boolean;
  onLinkClick?: () => void;
}

const UserDropdown: React.FC<UserDropdownProps> = ({
  username,
  role,
  onLogout,
  logoutLabel,
  flat = false,
  onLinkClick,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const visibleLinks = USER_MENU_LINKS.filter((link) => {
    if (!link.roles || link.roles.length === 0) return true;
    return link.roles.includes(role);
  });

  const getLabel = (key: string) => t(`header.userMenu.${key}`) || key;

  useEffect(() => {
    if (flat) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [flat]);

  const handleLinkClick = () => { setOpen(false); onLinkClick?.(); };

  const renderLink = (link: typeof USER_MENU_LINKS[0], className: string) =>
    link.external ? (
      <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer"
        className={className} onClick={handleLinkClick}>
        {link.icon && <span>{link.icon}</span>}
        <span>{getLabel(link.translationKey)}</span>
      </a>
    ) : (
      <Link key={link.href} to={link.href} className={className} onClick={handleLinkClick}>
        {link.icon && <span>{link.icon}</span>}
        <span>{getLabel(link.translationKey)}</span>
      </Link>
    );

  if (flat) {
    return (
      <div className="flex flex-col space-y-2 pt-2 border-t border-white/20">
        <div className="flex items-center space-x-2 text-sm font-semibold">
          <User className="h-4 w-4" /><span>{username}</span>
        </div>
        {visibleLinks.map((link) =>
          renderLink(link, 'flex items-center space-x-2 hover:text-pink-200 transition-colors duration-200 text-sm')
        )}
        <button onClick={() => { onLogout(); onLinkClick?.(); }}
          className="flex items-center space-x-2 text-left hover:text-pink-200 transition-colors duration-200 text-sm">
          <LogOut className="h-4 w-4" /><span>{logoutLabel}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}
        className="flex items-center space-x-1 px-3 py-2 border border-white/50 rounded-md hover:bg-white/10 transition-colors duration-200 text-sm">
        <User className="h-4 w-4" />
        <span>{username}</span>
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white text-gray-800 rounded-lg shadow-lg py-1 z-50 border border-gray-100">
          {visibleLinks.map((link) =>
            renderLink(link, 'flex items-center space-x-2 px-4 py-2 text-sm hover:bg-pink-50 hover:text-pink-600 transition-colors duration-150')
          )}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button onClick={() => { setOpen(false); onLogout(); }}
              className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors duration-150">
              <LogOut className="h-4 w-4" /><span>{logoutLabel}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;

